import React, { useState } from 'react';
import './App.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Basic Auth Component
const BasicAuth = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if already authenticated in this session
    return sessionStorage.getItem('basicAuthAuthenticated') === 'true';
  });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check credentials
    if (username === 'rhino' && password === 'forever') {
      setIsAuthenticated(true);
      sessionStorage.setItem('basicAuthAuthenticated', 'true');
      setError('');
    } else {
      setError('Invalid username or password');
      setPassword(''); // Clear password on error
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '400px'
        }}>
          <h2 style={{ 
            textAlign: 'center', 
            marginBottom: '1.5rem',
            color: '#333'
          }}>
            Authentication Required
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem',
                fontWeight: '500',
                color: '#333'
              }}>
                Username:
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                autoFocus
                required
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem',
                fontWeight: '500',
                color: '#333'
              }}>
                Password:
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                required
              />
            </div>
            {error && (
              <div style={{
                color: '#e74c3c',
                marginBottom: '1rem',
                textAlign: 'center',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Stripe configuration
const stripePromise = loadStripe('pk_test_51QMg3oI1z9q2Bw58BW2zrAlDPwAZKe7MQMTIZuoFLR5flaaaElTYyAkzgsN2lqCezYa45CESuMWg2vi3XywwxGPa00vtmLkZt8');

// API Configuration
const RhinoAPI = {
  baseURL: 'https://www.sayrhino.com/api/v1/loss_of_employment',
  
  getQuote: async (applicantData: any, monthlyRentDollars: number) => {
    try {
      // Validate required fields
      if (!applicantData.firstName || !applicantData.lastName || !applicantData.email) {
        console.log('Skipping API call - missing required applicant data');
        return {
          success: false,
          error: 'Missing required applicant information'
        };
      }

      // Clean phone number
      const cleanPhone = (applicantData.phone || '5551234567').replace(/\D/g, '');
      
      if (cleanPhone.length < 10) {
        console.log('API call failed - invalid phone number');
        return {
          success: false,
          error: 'Valid phone number is required'
        };
      }

      // Convert to cents and validate range
      const monthlyRentCents = Math.round(monthlyRentDollars * 100);
      
      if (monthlyRentCents < 10000 || monthlyRentCents > 1500000) {
        console.log('API call failed - rent amount out of range');
        return {
          success: false,
          error: 'Monthly rent must be between $100 and $15,000'
        };
      }

      const payload = {
        applicant: {
          first_name: applicantData.firstName.trim(),
          last_name: applicantData.lastName.trim(),
          email: applicantData.email.trim().toLowerCase(),
          telephone_number: cleanPhone
        },
        amount_insured_cents: monthlyRentCents
      };

      console.log('Quote API payload:', payload);

      const response = await fetch(`${RhinoAPI.baseURL}/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        
        let errorMessage = errorData.error?.message || errorData.message || 'Failed to get quote';
        
        // Handle specific error messages
        if (errorData.errors && errorData.errors.state) {
          const stateErrors = Array.isArray(errorData.errors.state) ? errorData.errors.state : [errorData.errors.state];
          const nyError = stateErrors.find((error: string) => error.toLowerCase().includes('new york'));
          if (nyError) {
            errorMessage = 'Coverage is not currently available in New York state';
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Quote API response:', data);

      return {
        success: true,
        quoteId: data.quote_id,
        monthlyPriceCents: data.monthly_price_cents,
        amountInsuredCents: data.amount_insured_cents,
        data: data
      };
    } catch (error: any) {
      console.error('Quote API error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  createPolicy: async (policyData: any) => {
    try {
      console.log('Creating policy with data:', policyData);

      const response = await fetch(`${RhinoAPI.baseURL}/policies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(policyData)
      });

      if (!response.ok) {
        // Check if response is JSON or HTML
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Failed to create policy';
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            console.error('Policy API Error Response:', errorData);
            
            errorMessage = errorData.error?.message || errorData.message || 'Failed to create policy';
            
            // Handle specific error messages
            if (errorData.errors) {
              if (errorData.errors.state) {
                const stateErrors = Array.isArray(errorData.errors.state) ? errorData.errors.state : [errorData.errors.state];
                const nyError = stateErrors.find((error: string) => error.toLowerCase().includes('new york'));
                if (nyError) {
                  errorMessage = 'Coverage is not currently available in New York state';
                }
              }
            }
          } catch (parseError) {
            console.error('Failed to parse error response as JSON:', parseError);
            errorMessage = `Server error (${response.status})`;
          }
        } else {
          // Handle HTML error response
          try {
            const htmlText = await response.text();
            console.error('Policy API HTML Error Response:', htmlText);
            
            // For 422 errors, it's likely a validation issue
            if (response.status === 422) {
              errorMessage = 'Validation error: Please check that all required information is provided correctly.';
            } else {
              errorMessage = `Server error (${response.status}). Please try again or contact support.`;
            }
          } catch (textError) {
            console.error('Failed to read error response:', textError);
            errorMessage = `Server error (${response.status})`;
          }
        }
        
        throw new Error(errorMessage);
      }

      const responseData = await response.json();

      console.log('Policy created successfully:', responseData);
      return {
        success: true,
        policy_id: responseData.policy_id,
        data: responseData
      };

    } catch (error: any) {
      console.error('Policy creation error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// Logo Component
const RhinoJettyLogo = () => (
  <div className="logo-container">
    <div className="logo-text">
      <span className="logo-rhino">Rhino</span>
      <span className="logo-plus">+</span>
      <span className="logo-jetty">Jetty</span>
    </div>
  </div>
);

// Progress Bar Component
const ProgressBar = ({ current, total }: { current: number; total: number }) => (
  <div className="progress-container">
    <div className="progress-text">Step {current} of {total}</div>
    <div className="progress-bar">
      <div 
        className="progress-fill" 
        style={{ width: `${(current / total) * 100}%` }}
      />
    </div>
  </div>
);

// Screen 1: Name
const NameScreen = ({ onNext, data, setData }: any) => {
  const [errors, setErrors] = useState<any>({});

  const validateNames = () => {
    const newErrors: any = {};
    
    if (!data.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!data.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateNames()) {
      onNext();
    }
  };

  return (
    <div className="survey-content">
      <ProgressBar current={1} total={11} />
      
      <div className="question-title">Let's get to know you real quick.</div>
      
      <div className="name-inputs">
        <div className="input-group">
          <label className="input-label">First Name</label>
          <input 
            type="text" 
            className={`text-input ${errors.firstName ? 'error' : ''}`}
            placeholder="John"
            value={data.firstName || ''}
            onChange={(e) => setData({...data, firstName: e.target.value})}
            autoFocus
          />
          {errors.firstName && <div className="input-error">{errors.firstName}</div>}
        </div>
        
        <div className="input-group">
          <label className="input-label">Last Name</label>
          <input 
            type="text" 
            className={`text-input ${errors.lastName ? 'error' : ''}`}
            placeholder="Doe"
            value={data.lastName || ''}
            onChange={(e) => setData({...data, lastName: e.target.value})}
          />
          {errors.lastName && <div className="input-error">{errors.lastName}</div>}
        </div>
      </div>
      
      <div className="button-container">
        <div className="button-row">
          <button 
            className="btn btn-primary" 
            onClick={handleNext}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

// Screen 2: Phone
const PhoneScreen = ({ onNext, onBack, data, setData }: any) => {
  const [errors, setErrors] = useState<any>({});

  const validatePhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return cleaned;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setData({...data, phone: formatted});
    
    // Clear errors on valid input
    if (validatePhone(e.target.value)) {
      setErrors({});
    }
  };

  const handleNext = () => {
    if (!data.phone?.trim()) {
      setErrors({ phone: 'Phone number is required' });
      return;
    }
    
    if (!validatePhone(data.phone)) {
      setErrors({ phone: 'Please enter a valid 10-digit phone number' });
      return;
    }
    
    onNext();
  };

  return (
    <div className="survey-content">
      <ProgressBar current={2} total={11} />
      
      <div className="question-title">Thanks! What's the best phone number to reach you?</div>
      
      <div className="input-group">
        <label className="input-label">Phone Number</label>
        <input 
          type="tel" 
          className={`text-input ${errors.phone ? 'error' : ''}`}
          placeholder="(555) 123-4567"
          value={data.phone || ''}
          onChange={handlePhoneChange}
          autoFocus
        />
        {errors.phone && <div className="input-error">{errors.phone}</div>}
        <div className="input-help">We use this to verify your quote and send status updates.</div>
      </div>
      
      <div className="button-container">
        <div className="button-row">
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleNext}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

// Screen 3: Email
const EmailScreen = ({ onNext, onBack, data, setData }: any) => {
  const [errors, setErrors] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({...data, email: e.target.value});
    
    // Clear errors on valid input
    if (validateEmail(e.target.value)) {
      setErrors({});
    }
  };

  const handleShowQuote = async () => {
    if (!data.email?.trim()) {
      setErrors({ email: 'Email address is required' });
      return;
    }
    
    if (!validateEmail(data.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);

    try {
      // Make the quote API call
      const result = await RhinoAPI.getQuote({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone
      }, 1500); // Default $1500 rent for initial quote

      if (result.success) {
        setData({
          ...data,
          quoteData: result
        });
        onNext();
      } else {
        setErrors({ email: result.error || 'Failed to get quote. Please try again.' });
      }
    } catch (error: any) {
      console.error('Quote error:', error);
      setErrors({ email: 'Failed to get quote. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="survey-content">
      <ProgressBar current={3} total={11} />
      
      <div className="question-title">And your email address?</div>
      
      <div className="input-group">
        <label className="input-label">Email</label>
        <input 
          type="email" 
          className={`text-input ${errors.email ? 'error' : ''}`}
          placeholder="john@example.com"
          value={data.email || ''}
          onChange={handleEmailChange}
          autoFocus
        />
        {errors.email && <div className="input-error">{errors.email}</div>}
        <div className="input-help">We will send your personalized rent protection quote here.</div>
      </div>
      
      <div className="button-container">
        <div className="button-row">
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleShowQuote}
            disabled={isLoading}
          >
            {isLoading ? 'Getting Quote...' : 'Show My Quote →'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Quote Results Screen
const QuoteScreen = ({ data, onNext }: any) => {
  const [monthlyRent, setMonthlyRent] = useState(1500);
  const [currentQuote, setCurrentQuote] = useState(data.quoteData);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);

  // Debounce timer ref
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleRentChange = async (newRent: number) => {
    setMonthlyRent(newRent);
    
    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Show loading state immediately
    setIsLoadingQuote(true);
    
    // Debounced API call - wait 500ms after user stops moving slider
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const result = await RhinoAPI.getQuote({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone
        }, newRent);

        if (result.success) {
          setCurrentQuote(result);
        }
      } catch (error) {
        console.error('Quote update error:', error);
      } finally {
        setIsLoadingQuote(false);
      }
    }, 500); // 500ms debounce delay
  };

  const monthlyPremium = currentQuote?.monthlyPriceCents ? currentQuote.monthlyPriceCents / 100 : 15;
  const yearlyRent = monthlyRent * 12;
  const dailyCostCents = currentQuote?.monthlyPriceCents ? Math.round(currentQuote.monthlyPriceCents / 30) : 1500;

  return (
    <div className="survey-content">
      <ProgressBar current={4} total={11} />
      
      <div className="question-title">Your Coverage Estimate</div>
      <div className="question-subtitle">Hi {data.firstName}! Here's your personalized quote:</div>
      
      {/* Rent Slider */}
      <div className="input-group">
        <label className="input-label">What is your monthly rent?</label>
        <div className="slider-container">
          <div className="slider-value">
            <span className="slider-amount">${monthlyRent.toLocaleString()}</span>
            <span className="slider-coverage">${yearlyRent.toLocaleString()}/year coverage</span>
          </div>
          <input 
            type="range"
            min="100"
            max="15000"
            step="50"
            value={monthlyRent}
            onChange={(e) => handleRentChange(parseInt(e.target.value))}
            className="slider-input"
          />
          <div className="slider-labels">
            <span>$100</span>
            <span>$15,000</span>
          </div>
        </div>
      </div>

      {/* Quote Display */}
      <div className="premium-display">
        <div className="premium-label">
          Monthly Premium
        </div>
        <div className="premium-amount">
          {isLoadingQuote ? 'Loading...' : `$${monthlyPremium.toFixed(2)}`}
        </div>
        <div className="premium-subtext">
          (${(dailyCostCents/100).toFixed(2)} per day)
          {currentQuote?.quoteId && (
            <div className="quote-info">
              <small>Quote ID: {currentQuote.quoteId}</small>
            </div>
          )}
        </div>
      </div>

      {/* Coverage Highlights */}
      <div className="policy-highlights">
        <h4>Coverage Highlights</h4>
        <ul>
          <li>Up to 12 months of rent payments</li>
          <li>90-day waiting period</li>
          <li>Coverage for involuntary job loss</li>
          <li>Quick claim processing</li>
          <li>Cancel anytime</li>
        </ul>
      </div>


      
      <div className="button-container">
        <button className="btn btn-primary" onClick={() => onNext(monthlyRent)}>
          Continue to Property Address →
        </button>
      </div>
    </div>
  );
};

// Property Address Screen
const PropertyAddressScreen = ({ onNext, onBack, data, setData }: any) => {
  const [errors, setErrors] = useState<any>({});
  const [propertyData, setPropertyData] = useState({
    streetAddress: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const validatePropertyData = () => {
    const newErrors: any = {};
    
    if (!propertyData.streetAddress?.trim()) {
      newErrors.streetAddress = 'Property address is required';
    }
    
    if (!propertyData.city?.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!propertyData.state?.trim()) {
      newErrors.state = 'State is required';
    } else if (propertyData.state.toUpperCase() === 'NY') {
      newErrors.state = 'Coverage not available in NY state.';
    }
    
    if (!propertyData.zipCode?.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(propertyData.zipCode.trim())) {
      newErrors.zipCode = 'Please enter a valid ZIP code';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validatePropertyData()) {
      setData({...data, property: propertyData});
      onNext();
    }
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <div className="survey-content">
      <ProgressBar current={5} total={11} />
      
      <div className="question-title">Where is your rental property?</div>
      <div className="question-subtitle">We need this to determine coverage availability in your area.</div>
      
      <div className="property-form">
        <div className="form-row">
          <div className="input-group">
            <label className="input-label">Property Address</label>
            <input 
              type="text" 
              className={`text-input ${errors.streetAddress ? 'error' : ''}`}
              placeholder="123 Main Street"
              value={propertyData.streetAddress}
              onChange={(e) => setPropertyData({...propertyData, streetAddress: e.target.value})}
              autoFocus
            />
            {errors.streetAddress && <div className="input-error">{errors.streetAddress}</div>}
          </div>
        </div>

        <div className="form-row city-state">
          <div className="input-group">
            <label className="input-label">City</label>
            <input 
              type="text" 
              className={`text-input ${errors.city ? 'error' : ''}`}
              placeholder="San Francisco"
              value={propertyData.city}
              onChange={(e) => setPropertyData({...propertyData, city: e.target.value})}
            />
            {errors.city && <div className="input-error">{errors.city}</div>}
          </div>

          <div className="input-group">
            <label className="input-label">State</label>
            <select 
              className={`text-input ${errors.state ? 'error' : ''}`}
              value={propertyData.state}
              onChange={(e) => setPropertyData({...propertyData, state: e.target.value})}
            >
              <option value="">Select State</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            {errors.state && <div className="input-error">{errors.state}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="input-group">
            <label className="input-label">ZIP Code</label>
            <input 
              type="text" 
              className={`text-input ${errors.zipCode ? 'error' : ''}`}
              placeholder="12345"
              value={propertyData.zipCode}
              onChange={(e) => setPropertyData({...propertyData, zipCode: e.target.value})}
              maxLength={10}
            />
            {errors.zipCode && <div className="input-error">{errors.zipCode}</div>}
          </div>
        </div>
      </div>
      
      <div className="button-container">
        <div className="button-row">
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleNext}
          >
            Continue to Eligibility
          </button>
        </div>
      </div>
    </div>
  );
};

// Eligibility Screen 1: Work Basics
const EligibilityScreen1 = ({ onNext, onBack, data, setData }: any) => {
  const [errors, setErrors] = useState<any>({});

  const handleAnswer = (field: string, value: string) => {
    const newData = {...data, [field]: value};
    setData(newData);

    // Check eligibility immediately when answer is selected
    if (field === 'works30Hours' && value === 'No') {
      setErrors({ eligibility: 'Unfortunately, you are not eligible for coverage at this time. Coverage requires working 30+ hours per week.' });
      return;
    }
    
    if (field === 'isW2Employee' && value === 'No') {
      setErrors({ eligibility: 'Unfortunately, you are not eligible for coverage at this time. Coverage requires being a W-2 employee.' });
      return;
    }

    if (field === 'currentJobDuration' && value === 'No') {
      setErrors({ eligibility: 'Unfortunately, you are not eligible for coverage at this time. Coverage requires working at your current job for at least 3 months.' });
      return;
    }

    // Clear errors if all answers are Yes
    if ((field === 'works30Hours' && value === 'Yes' && newData.isW2Employee === 'Yes' && newData.currentJobDuration === 'Yes') ||
        (field === 'isW2Employee' && value === 'Yes' && newData.works30Hours === 'Yes' && newData.currentJobDuration === 'Yes') ||
        (field === 'currentJobDuration' && value === 'Yes' && newData.works30Hours === 'Yes' && newData.isW2Employee === 'Yes')) {
      setErrors({});
    }
  };

  const handleNext = () => {
    if (!data.works30Hours) {
      setErrors({ works30Hours: 'Please answer this question' });
      return;
    }
    if (!data.isW2Employee) {
      setErrors({ isW2Employee: 'Please answer this question' });
      return;
    }
    if (!data.currentJobDuration) {
      setErrors({ currentJobDuration: 'Please answer this question' });
      return;
    }

    // Don't proceed if there's an eligibility error
    if (errors.eligibility) {
      return;
    }

    onNext();
  };

  return (
    <div className="survey-content">
      <ProgressBar current={6} total={11} />
      
      <div className="question-title">First, tell us a little about your job.</div>
      
      <div className="employment-questions">
        <div className="employment-question">
          <div className="question-text">Do you work 30+ hours per week?</div>
          <div className="radio-group inline">
            <button 
              className={`radio-button ${data.works30Hours === 'Yes' ? 'selected' : ''}`}
              onClick={() => handleAnswer('works30Hours', 'Yes')}
            >
              Yes
            </button>
            <button 
              className={`radio-button ${data.works30Hours === 'No' ? 'selected' : ''}`}
              onClick={() => handleAnswer('works30Hours', 'No')}
            >
              No
            </button>
          </div>
          {errors.works30Hours && <div className="input-error">{errors.works30Hours}</div>}
        </div>

        <div className="employment-question">
          <div className="question-text">Are you a W-2 employee?</div>
          <div className="radio-group inline">
            <button 
              className={`radio-button ${data.isW2Employee === 'Yes' ? 'selected' : ''}`}
              onClick={() => handleAnswer('isW2Employee', 'Yes')}
            >
              Yes
            </button>
            <button 
              className={`radio-button ${data.isW2Employee === 'No' ? 'selected' : ''}`}
              onClick={() => handleAnswer('isW2Employee', 'No')}
            >
              No
            </button>
          </div>
          {errors.isW2Employee && <div className="input-error">{errors.isW2Employee}</div>}
        </div>

        <div className="employment-question">
          <div className="question-text">Have you been working at your current job for at least 3 months?</div>
          <div className="radio-group inline">
            <button 
              className={`radio-button ${data.currentJobDuration === 'Yes' ? 'selected' : ''}`}
              onClick={() => handleAnswer('currentJobDuration', 'Yes')}
            >
              Yes
            </button>
            <button 
              className={`radio-button ${data.currentJobDuration === 'No' ? 'selected' : ''}`}
              onClick={() => handleAnswer('currentJobDuration', 'No')}
            >
              No
            </button>
          </div>
          {errors.currentJobDuration && <div className="input-error">{errors.currentJobDuration}</div>}
        </div>
      </div>

      {errors.eligibility && (
        <div className="eligibility-error">
          <div className="error-icon">❌</div>
          <div className="error-message">{errors.eligibility}</div>
        </div>
      )}
      
      <div className="button-container">
        <div className="button-row">
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleNext}
            disabled={!!errors.eligibility}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// Eligibility Screen 2: Company Status
const EligibilityScreen2 = ({ onNext, onBack, data, setData }: any) => {
  const [errors, setErrors] = useState<any>({});

  const handleAnswer = (field: string, value: string) => {
    const newData = {...data, [field]: value};
    setData(newData);

    // Check eligibility immediately when answer is selected
    if (field === 'companyEstablished' && value === 'No') {
      setErrors({ eligibility: 'Unfortunately, you are not eligible for coverage at this time. Coverage requires your employer to have been established for 3+ years.' });
      return;
    }
    
    if (field === 'pendingLayoffs' && value === 'Yes') {
      setErrors({ eligibility: 'Unfortunately, you are not eligible for coverage at this time. Coverage is not available if there are pending layoffs at your company.' });
      return;
    }

    // Clear errors if both answers are correct
    if ((field === 'companyEstablished' && value === 'Yes' && newData.pendingLayoffs === 'No') ||
        (field === 'pendingLayoffs' && value === 'No' && newData.companyEstablished === 'Yes')) {
      setErrors({});
    }
  };

  const handleNext = () => {
    if (!data.companyEstablished) {
      setErrors({ companyEstablished: 'Please answer this question' });
      return;
    }
    if (!data.pendingLayoffs) {
      setErrors({ pendingLayoffs: 'Please answer this question' });
      return;
    }

    // Don't proceed if there's an eligibility error
    if (errors.eligibility) {
      return;
    }

    onNext();
  };

  return (
    <div className="survey-content">
      <ProgressBar current={7} total={11} />
      
      <div className="question-title">A couple more quick checks.</div>
      
      <div className="employment-questions">
        <div className="employment-question">
          <div className="question-text">Has your employer been around for 3+ years?</div>
          <div className="radio-group inline">
            <button 
              className={`radio-button ${data.companyEstablished === 'Yes' ? 'selected' : ''}`}
              onClick={() => handleAnswer('companyEstablished', 'Yes')}
            >
              Yes
            </button>
            <button 
              className={`radio-button ${data.companyEstablished === 'No' ? 'selected' : ''}`}
              onClick={() => handleAnswer('companyEstablished', 'No')}
            >
              No
            </button>
          </div>
          {errors.companyEstablished && <div className="input-error">{errors.companyEstablished}</div>}
        </div>

        <div className="employment-question">
          <div className="question-text">Are there any pending layoffs at your company?</div>
          <div className="radio-group inline">
            <button 
              className={`radio-button ${data.pendingLayoffs === 'Yes' ? 'selected' : ''}`}
              onClick={() => handleAnswer('pendingLayoffs', 'Yes')}
            >
              Yes
            </button>
            <button 
              className={`radio-button ${data.pendingLayoffs === 'No' ? 'selected' : ''}`}
              onClick={() => handleAnswer('pendingLayoffs', 'No')}
            >
              No
            </button>
          </div>
          {errors.pendingLayoffs && <div className="input-error">{errors.pendingLayoffs}</div>}
        </div>
      </div>

      {errors.eligibility && (
        <div className="eligibility-error">
          <div className="error-icon">❌</div>
          <div className="error-message">{errors.eligibility}</div>
        </div>
      )}
      
      <div className="button-container">
        <div className="button-row">
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleNext}
            disabled={!!errors.eligibility}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// Eligibility Screen 3: Executive Role
const EligibilityScreen3 = ({ onNext, onBack, data, setData }: any) => {
  const [errors, setErrors] = useState<any>({});

  const handleAnswer = (field: string, value: string) => {
    setData({...data, [field]: value});

    // Check eligibility immediately when answer is selected
    if (field === 'isExecutive' && value === 'Yes') {
      setErrors({ eligibility: 'Unfortunately, executive roles are not eligible for coverage.' });
      return;
    }

    // Clear errors if answer is No
    if (field === 'isExecutive' && value === 'No') {
      setErrors({});
    }
  };

  const handleNext = () => {
    if (!data.isExecutive) {
      setErrors({ isExecutive: 'Please answer this question' });
      return;
    }

    // Don't proceed if there's an eligibility error
    if (errors.eligibility) {
      return;
    }

    // If they answered "No" to executive question, skip celebration and go straight to employer form
    if (data.isExecutive === 'No') {
      // Skip to employer screen (screen 8)
      onNext(true); // Pass flag to indicate skip celebration
    } else {
      onNext();
    }
  };

  return (
    <div className="survey-content">
      <ProgressBar current={8} total={11} />
      
      <div className="question-title">Final eligibility question — almost done!</div>
      
      <div className="employment-questions">
        <div className="employment-question">
          <div className="question-text">Are you a director or executive at your company?</div>
          <div className="radio-group inline">
            <button 
              className={`radio-button ${data.isExecutive === 'Yes' ? 'selected' : ''}`}
              onClick={() => handleAnswer('isExecutive', 'Yes')}
            >
              Yes
            </button>
            <button 
              className={`radio-button ${data.isExecutive === 'No' ? 'selected' : ''}`}
              onClick={() => handleAnswer('isExecutive', 'No')}
            >
              No
            </button>
          </div>
          {errors.isExecutive && <div className="input-error">{errors.isExecutive}</div>}
        </div>
      </div>

      {errors.eligibility && (
        <div className="eligibility-error">
          <div className="error-icon">❌</div>
          <div className="error-message">{errors.eligibility}</div>
        </div>
      )}
      
      <div className="button-container">
        <div className="button-row">
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleNext}
            disabled={!!errors.eligibility}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// Celebration Screen - "Buying Moment"
const CelebrationScreen = ({ data, onNext }: any) => {
  // Calculate daily cost from quote data
  const monthlyPriceCents = data.quoteData?.monthlyPriceCents || 2500; // fallback
  const dailyCost = Math.round(monthlyPriceCents / 30 / 100 * 100) / 100; // Convert to daily dollars
  const rentAmount = data.rentAmount || 1500;

  return (
    <div className="survey-content">
      <div className="celebration-header">
        <div className="celebration-title">You're eligible for up to ${rentAmount.toLocaleString()} in rent protection 🎉</div>
        <div className="celebration-subtitle">
          Based on your info, you can protect your rent for just ${dailyCost}/day.
        </div>
      </div>
      
      <div className="celebration-benefits">
        <div className="benefit-item">
          <div className="benefit-icon">🏠</div>
          <div className="benefit-text">Full rent coverage if you lose your job</div>
        </div>
        <div className="benefit-item">
          <div className="benefit-icon">⚡</div>
          <div className="benefit-text">Instant activation - no waiting period</div>
        </div>
        <div className="benefit-item">
          <div className="benefit-icon">🔒</div>
          <div className="benefit-text">Secure and confidential process</div>
        </div>
      </div>

      <div className="next-step-info">
        <div className="next-step-title">Let's just confirm where you work to activate your policy.</div>
        <div className="next-step-subtitle">No calls. No employer contact. Just a formality to keep things compliant.</div>
      </div>
      
      <div className="button-container">
        <button className="btn btn-primary celebration-cta" onClick={onNext}>
          Continue to Activate Protection →
        </button>
      </div>
    </div>
  );
};

// Employer Details Screen
const EmployerScreen = ({ data, setData, onNext, onBack }: any) => {
  const [errors, setErrors] = useState<any>({});
  const [employerData, setEmployerData] = useState({
    employerName: '',
    jobTitle: '',
    startDate: '',
    workAddress: '',
    workCity: '',
    workState: ''
  });

  const validateEmployerData = () => {
    const newErrors: any = {};
    
    if (!employerData.employerName?.trim()) {
      newErrors.employerName = 'Employer name is required';
    }
    
    if (!employerData.jobTitle?.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }
    
    if (!employerData.startDate) {
      newErrors.startDate = 'Start date is required';
    } else {
      // Validate that start date is at least 90 days ago
      const startDate = new Date(employerData.startDate);
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      
      if (startDate > ninetyDaysAgo) {
        newErrors.startDate = 'You must have been working at your current job for at least 90 days to be eligible for coverage';
      }
    }
    
    if (!employerData.workAddress?.trim()) {
      newErrors.workAddress = 'Work address is required';
    }
    
    if (!employerData.workCity?.trim()) {
      newErrors.workCity = 'City is required';
    }
    
    if (!employerData.workState?.trim()) {
      newErrors.workState = 'State is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateEmployerData()) {
      setData({...data, employer: employerData});
      onNext();
    }
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <div className="survey-content">
      <ProgressBar current={9} total={11} />
      
      <div className="question-title">Final step: confirm where you work</div>
      <div className="question-subtitle">This is just for compliance. We never contact your employer.</div>
      
      <div className="qualification-confirmation">
        ✅ You qualify for coverage based on your answers.
      </div>
      
      <div className="employer-form">
        <div className="form-row">
          <div className="input-group">
            <label className="input-label">Employer Name</label>
            <input 
              type="text" 
              className={`text-input ${errors.employerName ? 'error' : ''}`}
              placeholder="Acme Corporation"
              value={employerData.employerName}
              onChange={(e) => setEmployerData({...employerData, employerName: e.target.value})}
              autoFocus
            />
            {errors.employerName && <div className="input-error">{errors.employerName}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="input-group">
            <label className="input-label">Job Title</label>
            <input 
              type="text" 
              className={`text-input ${errors.jobTitle ? 'error' : ''}`}
              placeholder="Software Engineer"
              value={employerData.jobTitle}
              onChange={(e) => setEmployerData({...employerData, jobTitle: e.target.value})}
            />
            {errors.jobTitle && <div className="input-error">{errors.jobTitle}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="input-group">
            <label className="input-label">Start Date</label>
            <input 
              type="date" 
              className={`text-input ${errors.startDate ? 'error' : ''}`}
              value={employerData.startDate}
              onChange={(e) => setEmployerData({...employerData, startDate: e.target.value})}
            />
            {errors.startDate && <div className="input-error">{errors.startDate}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="input-group">
            <label className="input-label">Work Address</label>
            <input 
              type="text" 
              className={`text-input ${errors.workAddress ? 'error' : ''}`}
              placeholder="123 Main Street"
              value={employerData.workAddress}
              onChange={(e) => setEmployerData({...employerData, workAddress: e.target.value})}
            />
            {errors.workAddress && <div className="input-error">{errors.workAddress}</div>}
          </div>
        </div>

        <div className="form-row city-state">
          <div className="input-group">
            <label className="input-label">City</label>
            <input 
              type="text" 
              className={`text-input ${errors.workCity ? 'error' : ''}`}
              placeholder="San Francisco"
              value={employerData.workCity}
              onChange={(e) => setEmployerData({...employerData, workCity: e.target.value})}
            />
            {errors.workCity && <div className="input-error">{errors.workCity}</div>}
          </div>
          
          <div className="input-group">
            <label className="input-label">State</label>
            <select 
              className={`text-input ${errors.workState ? 'error' : ''}`}
              value={employerData.workState}
              onChange={(e) => setEmployerData({...employerData, workState: e.target.value})}
            >
              <option value="">Select State</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            {errors.workState && <div className="input-error">{errors.workState}</div>}
          </div>
        </div>
      </div>
      
      <div className="button-container">
        <div className="button-row">
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button className="btn btn-primary employer-cta" onClick={handleNext}>
            Lock In My Coverage
          </button>
        </div>
      </div>
    </div>
  );
};

// Stripe Payment Form Component
const StripePaymentForm = ({ onNext, onBack, data }: any) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [billingInfo, setBillingInfo] = useState({
    name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
    email: data.email || '',
    zipCode: ''
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    // Validate billing info
    const newErrors: any = {};
    if (!billingInfo.name.trim()) {
      newErrors.name = 'Name on card is required';
    }
    if (!billingInfo.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsProcessing(true);
    setErrors({});

    try {
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingInfo.name,
          email: billingInfo.email,
          address: {
            postal_code: billingInfo.zipCode,
          },
        },
      });

      if (error) {
        setErrors({ payment: error.message });
        setIsProcessing(false);
        return;
      }

      // Payment method created successfully
      console.log('Payment Method Created:', paymentMethod);
      
      // Validate we have required data
      if (!data.quoteData?.quoteId) {
        setErrors({ payment: 'Quote data missing. Please refresh and try again.' });
        setIsProcessing(false);
        return;
      }

      if (!data.firstName || !data.lastName || !data.email) {
        setErrors({ payment: 'Personal information missing. Please go back and complete all fields.' });
        setIsProcessing(false);
        return;
      }

      if (!data.phone || data.phone.replace(/\D/g, '').length < 10) {
        setErrors({ payment: 'Valid phone number required. Please go back and complete all fields.' });
        setIsProcessing(false);
        return;
      }

      if (!data.employer?.workAddress || !data.employer?.workCity || !data.employer?.workState) {
        setErrors({ payment: 'Employer information missing. Please go back and complete all fields.' });
        setIsProcessing(false);
        return;
      }

      // Validate employment start date is at least 90 days ago
      if (data.employer?.startDate) {
        const startDate = new Date(data.employer.startDate);
        const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
        
        if (startDate > ninetyDaysAgo) {
          setErrors({ payment: 'Employment start date must be at least 90 days ago for coverage eligibility.' });
          setIsProcessing(false);
          return;
        }
      }

      // Check NY state restriction for property location (not employment location)
      const propertyState = (data.employer?.workState || "CA").toUpperCase();
      if (propertyState === 'NY') {
        setErrors({ payment: 'Coverage is not currently available for properties in New York state. We are working to expand coverage to additional states.' });
        setIsProcessing(false);
        return;
      }

      // Prepare data for policy creation - matching Ruby model structure
      const phoneDigits = data.phone?.replace(/\D/g, '') || '';
      
      if (phoneDigits.length < 10) {
        setErrors({ payment: 'Valid 10-digit phone number is required' });
        setIsProcessing(false);
        return;
      }
      
      // Validate required data objects
      if (!data.quoteData?.quoteId && !data.quoteData?.id) {
        setErrors({ payment: 'Quote information is missing. Please restart the process.' });
        setIsProcessing(false);
        return;
      }
      
      if (!data.property) {
        setErrors({ payment: 'Property address is required. Please go back and complete the property address.' });
        setIsProcessing(false);
        return;
      }
      
      if (!data.employer) {
        setErrors({ payment: 'Employer information is required. Please go back and complete employer details.' });
        setIsProcessing(false);
        return;
      }
      const currentDate = new Date();
      const inceptionDate = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD
      const inceptionTime = currentDate.toTimeString().split(' ')[0]; // HH:MM:SS
      const expiryDate = new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate())
        .toISOString().split('T')[0]; // One year from now
      
      // Generate reference number (simple timestamp-based)
      const referenceNumber = `LOE-${Date.now()}`;
      
      // Calculate employment start date (must be at least 90 days ago)
      let employmentStartDate = data.employer?.startDate || 
        new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 120 days ago as fallback
      
      // Ensure employment start date is at least 90 days ago
      const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
      const employmentDate = new Date(employmentStartDate);
      if (employmentDate > ninetyDaysAgo) {
        employmentStartDate = new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      }
      
      // Use realistic date of birth (30 years old)
      const dateOfBirth = new Date(currentDate.getFullYear() - 30, 0, 1).toISOString().split('T')[0];
      
      const policyPayload = {
        quote_id: data.quoteData?.quoteId || data.quoteData?.id,
        applicant: {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          telephone_number: phoneDigits.padStart(10, '0').slice(0, 10),
          date_of_birth: dateOfBirth,
          street_address: data.property?.streetAddress || "123 Main St",
          city: data.property?.city || "San Francisco",
          state: (data.property?.state || "CA").toUpperCase()
        },
        employment: {
          employer_name: data.employer?.employerName || "Unknown Company",
          employer_street_address: data.employer?.workAddress || "123 Main St",
          employer_city: data.employer?.workCity || "San Francisco",
          employer_state: (data.employer?.workState || "CA").toUpperCase(),
          occupation: data.employer?.jobTitle || "Employee",
          employment_start_date: employmentStartDate,
          six_months_employment: data.currentJobDuration === 'Yes',
          thirty_hours_per_week: data.works30Hours === 'Yes',
          w2_employee: data.isW2Employee === 'Yes',
          director_or_executive: data.isExecutive === 'Yes',
          pending_layoffs: data.pendingLayoffs === 'Yes',
          employer_incorporated_three_years: data.companyEstablished === 'Yes'
        },
        property: {
          monthly_rent: Math.round((data.rentAmount || 1500) * 100), // Convert to cents
          ...(data.landlord?.name && { landlord_name: data.landlord.name }),
          ...(data.landlord?.email && { landlord_email: data.landlord.email })
        },
        payment: {
          payment_method_token: paymentMethod.id
        }
      };

      // Validate required fields before sending (nested structure)
      const validationErrors = [];
      
      if (!policyPayload.quote_id) {
        validationErrors.push('Quote ID is missing');
      }
      
      if (!policyPayload.applicant?.first_name) {
        validationErrors.push('First name is missing');
      }
      
      if (!policyPayload.applicant?.last_name) {
        validationErrors.push('Last name is missing');
      }
      
      if (!policyPayload.applicant?.email) {
        validationErrors.push('Email is missing');
      }
      
      if (!policyPayload.applicant?.telephone_number) {
        validationErrors.push('Phone number is missing');
      }
      
      if (!policyPayload.applicant?.date_of_birth) {
        validationErrors.push('Date of birth is missing');
      }
      
      if (!policyPayload.applicant?.street_address) {
        validationErrors.push('Property address is missing');
      }
      
      if (!policyPayload.employment?.employer_name) {
        validationErrors.push('Employer name is missing');
      }
      
      if (!policyPayload.property?.monthly_rent) {
        validationErrors.push('Monthly rent is missing');
      }
      
      if (!policyPayload.payment?.payment_method_token) {
        validationErrors.push('Payment method is missing');
      }
      
      if (validationErrors.length > 0) {
        console.error('Policy validation errors:', validationErrors);
        setErrors({ payment: `Missing required information: ${validationErrors.join(', ')}` });
        setIsProcessing(false);
        return;
      }

      console.log('=== POLICY CREATION DEBUG ===');
      console.log('Raw form data:', data);
      console.log('Quote data available:', data.quoteData);
      console.log('Policy payload being sent:', JSON.stringify(policyPayload, null, 2));
      console.log('=== END DEBUG ===');

      // Create policy
      const policyResult = await RhinoAPI.createPolicy(policyPayload);
      
      if (!policyResult.success) {
        console.error('Policy creation failed:', policyResult);
        setErrors({ payment: policyResult.error || 'Failed to create policy' });
        setIsProcessing(false);
        return;
      }

      // Store policy result for success screen
      const updatedData = {
        ...data,
        policyResult: policyResult,
        paymentMethod: paymentMethod
      };
      
      // Success - move to next screen with policy data
      onNext(updatedData);
      
    } catch (error: any) {
      setErrors({ payment: error.message || 'Payment processing failed' });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="survey-content">
      <ProgressBar current={11} total={11} />
      
      <div className="question-title">Enter your payment info</div>
      <div className="question-subtitle">
        Your payment information is secure and encrypted. We'll never store your card details.
      </div>
      
      <form onSubmit={handleSubmit} className="payment-form">
        <div className="billing-info">
          <div className="input-group">
            <label className="input-label">Name on Card</label>
            <input
              type="text"
              className={`text-input ${errors.name ? 'error' : ''}`}
              value={billingInfo.name}
              onChange={(e) => setBillingInfo({...billingInfo, name: e.target.value})}
              placeholder="John Doe"
            />
            {errors.name && <div className="input-error">{errors.name}</div>}
          </div>

          <div className="input-group">
            <label className="input-label">ZIP Code</label>
            <input
              type="text"
              className={`text-input ${errors.zipCode ? 'error' : ''}`}
              value={billingInfo.zipCode}
              onChange={(e) => setBillingInfo({...billingInfo, zipCode: e.target.value})}
              placeholder="12345"
              maxLength={5}
            />
            {errors.zipCode && <div className="input-error">{errors.zipCode}</div>}
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Card Information</label>
          <div className="stripe-card-element">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#1e293b',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Inter", sans-serif',
                    '::placeholder': {
                      color: '#94a3b8',
                    },
                  },
                  invalid: {
                    color: '#ef4444',
                  },
                },
                hidePostalCode: true,
              }}
            />
          </div>
          {errors.payment && <div className="input-error">{errors.payment}</div>}
        </div>

        <div className="trust-message">
          🔒 Your payment is encrypted and securely processed.
        </div>
        
        <div className="button-container">
          <div className="button-row">
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back
            </button>
            <button 
              type="submit" 
              className="btn btn-primary payment-cta" 
              disabled={isProcessing || !stripe}
            >
              {isProcessing ? 'Processing...' : 'Start Coverage'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// Payment Screen Wrapper (with Stripe Elements)
const PaymentScreen = ({ onNext, onBack, data, setData }: any) => {
  const handleNext = (updatedData?: any) => {
    if (updatedData) {
      setData(updatedData);
    }
    onNext();
  };

  return (
    <Elements stripe={stripePromise}>
      <StripePaymentForm onNext={handleNext} onBack={onBack} data={data} />
    </Elements>
  );
};

// Waiting Period Acknowledgment Screen
const WaitingPeriodScreen = ({ onNext, onBack }: any) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handleNext = () => {
    if (acknowledged) {
      onNext();
    }
  };

  return (
    <div className="survey-content">
      <ProgressBar current={10} total={11} />
      
      <div className="question-title">Almost done — one quick box</div>
      
      <div className="waiting-period-content">
        <div className="checkbox-container" onClick={() => setAcknowledged(!acknowledged)}>
          <div className={`checkbox ${acknowledged ? 'checked' : ''}`}>
            {acknowledged && '✓'}
          </div>
          <div className="checkbox-text">I understand the 90-day waiting period</div>
        </div>
        
        <button 
          className="details-toggle" 
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? 'Hide details' : 'Show details'}
        </button>
        
        {showDetails && (
          <div className="waiting-period-details">
            <h4>About the 90-day waiting period</h4>
            <p>
              The 90-day waiting period means you cannot file a claim during 
              your first 90 days of coverage. This helps keep premiums 
              affordable for everyone while preventing immediate claims after 
              unexpected job loss.
            </p>
            <p>
              <strong>After 90 days:</strong> You'll have full coverage benefits including 
              monthly rent payments up to your coverage limit if you 
              experience qualifying job loss.
            </p>
          </div>
        )}
      </div>
      
      <div className="button-container">
        <div className="button-row">
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleNext}
            disabled={!acknowledged}
          >
            Complete Purchase
          </button>
        </div>
      </div>
    </div>
  );
};

// Final Success Screen
const FinalSuccessScreen = ({ data }: any) => {
  const policyId = data.policyResult?.policy_id;
  const [copied, setCopied] = useState(false);
  const [landlordData, setLandlordData] = useState({
    name: '',
    email: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const copyPolicyDetails = async () => {
    try {
      const policyText = `Policy ID: ${policyId}
Monthly Coverage: $${(data.rentAmount || 1500).toLocaleString()}
Monthly Premium: $${((data.quoteData?.monthlyPriceCents || 2500) / 100).toFixed(2)}${data.employer?.employerName ? `
Employer: ${data.employer.employerName}` : ''}`;
      
      await navigator.clipboard.writeText(policyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const sendLandlordNotification = async () => {
    if (!landlordData.name || !landlordData.email) {
      return;
    }

    setSending(true);
    try {
      // Simulate API call for landlord notification
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      console.error('Failed to send notification:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="survey-content">
      <div className="success-content">
        <div className="success-icon">🎉</div>
        <div className="success-title">Coverage Activated!</div>
        <div className="success-subtitle">
          Congratulations {data.firstName}! Your rent protection is now active.
        </div>
        
        <div className="coverage-summary">
          <div className="summary-item">
            <div className="summary-label">Policy ID</div>
            <div className="summary-value-with-copy">
              <span className="summary-value">{policyId}</span>
              <button 
                className="copy-button" 
                onClick={copyPolicyDetails}
                title="Copy Policy Details"
              >
                📋
              </button>
              {copied && <span className="copy-feedback">Policy details copied!</span>}
            </div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Monthly Coverage</div>
            <div className="summary-value">${(data.rentAmount || 1500).toLocaleString()}</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Monthly Premium</div>
            <div className="summary-value">${((data.quoteData?.monthlyPriceCents || 2500) / 100).toFixed(2)}</div>
          </div>
          {data.employer?.employerName && (
            <div className="summary-item">
              <div className="summary-label">Employer</div>
              <div className="summary-value">{data.employer.employerName}</div>
            </div>
          )}
        </div>

        <div className="next-steps">
          <div className="next-steps-title">What happens next?</div>
          <div className="next-step-item">📧 Check your email for policy documents</div>
          <div className="next-step-item">💳 Your first payment will be processed shortly</div>
          <div className="next-step-item">🛡️ Coverage begins immediately</div>
          <div className="next-step-item">⏰ Remember: 90-day waiting period applies</div>
        </div>

        <div className="landlord-notification-divider">⸻</div>

        <div className="landlord-notification-section">
          <div className="landlord-notification-title">
            📣 Want us to inform your landlord? (Optional)
          </div>
          <div className="landlord-notification-subtitle">
            We will send them a one-time confirmation that your rent protection is now active.
          </div>

          <div className="landlord-form">
            <div className="input-group">
              <label className="input-label">Landlord Name</label>
              <input
                type="text"
                className="text-input"
                placeholder="John Smith"
                value={landlordData.name}
                onChange={(e) => setLandlordData({...landlordData, name: e.target.value})}
              />
            </div>

            <div className="input-group">
              <label className="input-label">Landlord Email</label>
              <input
                type="email"
                className="text-input"
                placeholder="landlord@example.com"
                value={landlordData.email}
                onChange={(e) => setLandlordData({...landlordData, email: e.target.value})}
              />
            </div>

            <button 
              className="btn btn-primary landlord-cta"
              onClick={sendLandlordNotification}
              disabled={!landlordData.name || !landlordData.email || sending || sent}
            >
              {sending ? 'Sending...' : sent ? 'Sent ✓' : 'Notify My Landlord'}
            </button>
          </div>

          <div className="landlord-disclaimer">
            🔒 We will never spam your landlord or share personal data.
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [data, setData] = useState<any>({});

  const screens = [
    <NameScreen 
      onNext={() => setCurrentScreen(1)} 
      data={data} 
      setData={setData} 
    />,
    <PhoneScreen 
      onNext={() => setCurrentScreen(2)} 
      onBack={() => setCurrentScreen(0)}
      data={data} 
      setData={setData} 
    />,
    <EmailScreen 
      onNext={() => setCurrentScreen(3)} 
      onBack={() => setCurrentScreen(1)}
      data={data} 
      setData={setData} 
    />,
    <QuoteScreen 
      data={data}
      onNext={(rentAmount?: number) => {
        if (rentAmount) {
          setData({...data, rentAmount});
        }
        setCurrentScreen(4);
      }}
    />,
    <PropertyAddressScreen 
      onNext={() => setCurrentScreen(5)} 
      onBack={() => setCurrentScreen(3)}
      data={data} 
      setData={setData} 
    />,
    <EligibilityScreen1 
      onNext={() => setCurrentScreen(6)} 
      onBack={() => setCurrentScreen(4)}
      data={data} 
      setData={setData} 
    />,
    <EligibilityScreen2 
      onNext={() => setCurrentScreen(7)} 
      onBack={() => setCurrentScreen(5)}
      data={data} 
      setData={setData} 
    />,
    <EligibilityScreen3 
      onNext={(skipCelebration?: boolean) => skipCelebration ? setCurrentScreen(9) : setCurrentScreen(8)} 
      onBack={() => setCurrentScreen(6)}
      data={data} 
      setData={setData} 
    />,
    <CelebrationScreen 
      data={data}
      onNext={() => setCurrentScreen(9)}
    />,
    <EmployerScreen 
      data={data}
      setData={setData}
      onNext={() => setCurrentScreen(10)}
      onBack={() => setCurrentScreen(7)}
    />,
    <WaitingPeriodScreen 
      onNext={() => setCurrentScreen(11)}
      onBack={() => setCurrentScreen(9)}
    />,
    <PaymentScreen 
      onNext={() => setCurrentScreen(12)}
      onBack={() => setCurrentScreen(10)}
      data={data}
      setData={setData}
    />,
    <FinalSuccessScreen 
      data={data}
    />
  ];

  return (
    <BasicAuth>
      <div className="app">
        <RhinoJettyLogo />
        <div className="main-content">
          <div className="phone-container">
            {screens[currentScreen]}
          </div>
        </div>
      </div>
    </BasicAuth>
  );
}

export default App;
